import React, { useState, createContext, useEffect, useRef, useMemo } from 'react';
import { Box, Stack, Step, StepLabel, Stepper, Button, Container } from '@mui/material';
import { fetchCountryData } from '../../api/util';
import { approveDocusign, getCompanyDetailsFromVAT } from '../../api/registration';
import { useLocation } from 'react-router-dom';

import BeforeYouStart from './1.BeforeYouStart';
import CompanyDetails from './2.CompanyDetails';
import ActivateAccount from './3.ActivateAccount';

export const RegistrationContext = createContext(null);

const RegistrationContainer = () => {
  const [formData, setFormData] = useState({
    email: '',
    verificationCode: '',
    name: '',
    eoriNumber: '',
    address: '',
    town: '',
    postcode: '',
    country: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
  });

  const [docusignReturnedEmailAddress, setDocusignReturnedEmailAddress] = useState();
  const [error, setError] = useState('');
  const [countries, setCountries] = useState([]);
  const [activeStep, setActiveStep] = useState(0);

  const [checkVATOrEORI, setCheckVATOrEORI] = useState('');
  const [fetchCompanyTimeout, setFetchCompanyTimeout] = useState(null);
  const [isTransferryCustomer, setIsTransferryCustomer] = useState(false);

  const apiCalledRef = useRef(false);

  const newErrors = {};

  const steps = ['Before you start', 'Company Details', 'Activate Account'];

  const loadCountryData = async () => {
    try {
      const countryList = await fetchCountryData();
      setCountries(countryList);
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const location = useLocation();
  const getQueryParams = () => {
    const searchParams = new URLSearchParams(location.search);
    return {
      state: searchParams.get('state'),
      event: searchParams.get('event'),
    };
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && !email.endsWith('.') && !email.endsWith('@');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const mappedName = name === 'y-email' ? 'email' : name;

    // used to check the field that the user has just changed
    if (name === 'vatNumber') {
      setCheckVATOrEORI('vat');
    } else if (name === 'eoriNumber') {
      setCheckVATOrEORI('eori');
    }

    setFormData({ ...formData, [mappedName]: value });

    if (mappedName === 'email') setError('');
  };

  useEffect(() => {
    loadCountryData();
    const { state, event } = getQueryParams();
    if (event === 'signing_complete' && state) {
      if (apiCalledRef.current) {
        console.warn('API already called, skipping...');
      } else {
        console.log('API not yet called. Proceeding to call approveDocusign...');
        apiCalledRef.current = true; // Mark API as called
        setActiveStep(2); // Move to step 4
        if (!isValidEmail(formData.contactEmail)) {
          newErrors['contactEmail'] = 'Please enter a valid email address.';
        }
        approveDocusign(state)
          .then((data) => {
            console.log('API call successful:', data);
            setDocusignReturnedEmailAddress(data.email);
          })
          .catch((error) => {
            console.error('API call failed:', error);
          });
      }
    }
  }, [location]);

  useEffect(() => {
    if (checkVATOrEORI !== '') {
      // Start fetching company data
      setFetchCompanyTimeout(
        setTimeout(() => {
          const isCheckingVAT = checkVATOrEORI === 'vat';
          const isCheckingEORI = checkVATOrEORI === 'eori';

          let numberToCheck = 'none';

          // if user has just changed VAT or EORI number, use that value
          if (isCheckingVAT && formData.vatNumber) {
            numberToCheck = formData.vatNumber;
          } else if (isCheckingEORI && formData.eoriNumber) {
            numberToCheck = formData.eoriNumber;
          }

          getCompanyDetailsFromVAT(numberToCheck)
            .then((data) => {
              // Handle successful response
              const isTransferry = !!data?.isTransferry;
              setIsTransferryCustomer(isTransferry);
            })
            .catch((error) => {
              console.error('Error fetching company data:', error);
            });
        }, 1000) // Delay the API call by 1 second
      );

      setCheckVATOrEORI(''); // Reset the flag
    }

    // clear timeout on unmount
    return () => {
      if (fetchCompanyTimeout) {
        clearTimeout(fetchCompanyTimeout);
      }
    };
  }, [checkVATOrEORI]);

  const registrationContextValue = useMemo(
    () => ({
      formData,
      setFormData,
      handleChange,
      error,
      setError,
      setActiveStep,
      countries,
      newErrors,
      getQueryParams,
      isValidEmail,
      docusignReturnedEmailAddress,
      isTransferryCustomer,
    }),
    [
      formData,
      setFormData,
      handleChange,
      error,
      setError,
      setActiveStep,
      countries,
      newErrors,
      getQueryParams,
      isValidEmail,
      docusignReturnedEmailAddress,
      isTransferryCustomer,
    ]
  );

  return (
    <RegistrationContext.Provider value={registrationContextValue}>
      <Container maxWidth="lg">
        <Box sx={{ p: 4 }}>
          <Stack direction="row" spacing={4}>
            <Box>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((label, index) => (
                  <Step key={index}>
                    <StepLabel>
                      <Button
                        disabled={index !== activeStep} // Disable all steps except the active one
                        sx={{
                          textTransform: 'none',
                          color: index === activeStep ? 'primary.main' : 'text.disabled',
                        }}
                      >
                        {label}
                      </Button>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Box sx={{ flex: 1, p: 2, border: '1px solid #ddd', borderRadius: 2 }}>
              {activeStep === 0 && <BeforeYouStart />}
              {activeStep === 1 && <CompanyDetails />}
              {activeStep === 2 && <ActivateAccount />}
            </Box>
          </Stack>
        </Box>
      </Container>
    </RegistrationContext.Provider>
  );
};

export default RegistrationContainer;
